import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Check,
  AlertTriangle,
  Clock,
  X,
  RefreshCw,
  MessageSquare,
  Send,
  Sparkles,
  ChevronUp,
  ChevronRight,
  Languages,
  FileText,
  Globe,
  Loader2,
  Trash2,
  StopCircle,
  Smartphone,
  Monitor,
  Share2,
  Filter,
  Database,
  Search,
  Plus,
  Copy,
  Edit,
} from 'lucide-react';
import { api } from '../../services/api';

// Format duration in ms to human readable string
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

type TabType = 'app-strings' | 'website-content' | 'manage-strings';

// Language names for display - all supported languages
const LANGUAGE_NAMES: Record<string, string> = {
  // Western Europe
  de: 'German',
  fr: 'French',
  nl: 'Dutch',
  // Southern Europe
  es: 'Spanish',
  it: 'Italian',
  pt: 'Portuguese',
  el: 'Greek',
  // Nordic
  sv: 'Swedish',
  no: 'Norwegian',
  da: 'Danish',
  fi: 'Finnish',
  // Central/Eastern Europe
  pl: 'Polish',
  cs: 'Czech',
  hu: 'Hungarian',
  ro: 'Romanian',
  sk: 'Slovak',
  sl: 'Slovenian',
  hr: 'Croatian',
  sr: 'Serbian',
  // East Asia
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  // Southeast Asia
  th: 'Thai',
  id: 'Indonesian',
  ms: 'Malay',
  // South Asia
  hi: 'Hindi',
  // Middle East
  ar: 'Arabic',
  he: 'Hebrew',
  tr: 'Turkish',
  // Southeast Asia additions
  vi: 'Vietnamese',
  // Eastern Europe additions
  bg: 'Bulgarian',
};

// Pre-built prompts for the translation assistant
const TRANSLATION_PROMPTS = [
  // Quality Checks
  { label: 'Is this natural?', prompt: 'Does this translation sound natural to a native speaker? What would a native say instead?', category: 'quality' },
  { label: 'Check grammar', prompt: 'Check this translation for grammar errors and suggest corrections.', category: 'quality' },
  { label: 'Rate formality', prompt: 'Is the formality level appropriate? Should it be more formal or casual?', category: 'quality' },
  // Alternatives
  { label: 'More formal', prompt: 'Suggest a more formal version of this translation.', category: 'alternatives' },
  { label: 'More casual', prompt: 'Suggest a more casual, conversational version.', category: 'alternatives' },
  { label: '3 alternatives', prompt: 'Give me 3 alternative ways to translate this, with pros/cons of each.', category: 'alternatives' },
  // Cultural Context
  { label: 'Cultural check', prompt: 'Is this culturally appropriate for the target audience? Any regional considerations?', category: 'cultural' },
  { label: 'Regional variants', prompt: 'How would this differ between regional variants of this language?', category: 'cultural' },
  // Troubleshooting
  { label: 'Why failed?', prompt: 'This translation keeps failing. Analyze the source text for issues that might cause translation problems.', category: 'troubleshooting' },
  { label: 'Fix encoding', prompt: 'Check for encoding issues, special characters, or formatting that might cause problems.', category: 'troubleshooting' },
  { label: 'Simplify source', prompt: 'Suggest how to simplify the English source text to get better translations.', category: 'troubleshooting' },
];

const PROMPT_CATEGORY_COLORS: Record<string, string> = {
  quality: 'bg-green-100 text-green-700 hover:bg-green-200',
  alternatives: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  cultural: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  troubleshooting: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
};

// ============================================
// APP STRINGS TAB CONTENT
// ============================================

// Frontend-driven translation state for app strings
interface AppStringTranslationState {
  isRunning: boolean;
  items: Array<{ id: string; key: string; category: string | null }>;
  currentIndex: number;
  completed: number;
  failed: number;
  currentItem: string | null;
  lastError: string | null;
  startedAt: number | null;
  forceMode: boolean;
}

// Content string type for CRUD operations
interface ContentString {
  id: string;
  key: string;
  english_text: string;
  category: string | null;
  description: string | null;
  max_length: number | null;
  placeholders: Record<string, string> | null;
  created_at: string;
  updated_at: string;
  translation_count: number;
}

// App String Language card component
function AppStringLanguageCard({
  language,
  stats,
  isSelected,
  onClick,
}: {
  language: string;
  stats: { translated: number; missing: number; total: number };
  isSelected: boolean;
  onClick: () => void;
}) {
  const percentage = stats.total > 0 ? Math.round((stats.translated / stats.total) * 100) : 0;
  const hasIssues = stats.missing > 0;

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-lg border text-left transition-all hover:shadow-md ${
        isSelected
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
          : hasIssues
          ? 'border-amber-200 bg-white hover:border-amber-300'
          : 'border-green-200 bg-white hover:border-green-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-900">{LANGUAGE_NAMES[language] || language}</span>
        <span className="text-xs font-medium text-gray-500 uppercase">{language}</span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full transition-all ${
            percentage === 100 ? 'bg-green-500' : percentage > 50 ? 'bg-primary-500' : 'bg-amber-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className={percentage === 100 ? 'text-green-600 font-medium' : 'text-gray-600'}>
          {percentage}% complete
        </span>
        <span className="text-gray-500">
          {stats.translated}/{stats.total}
        </span>
      </div>
    </button>
  );
}

// Platform badge component
function PlatformBadge({ platforms }: { platforms: string }) {
  const platformList = platforms.split(',').map(p => p.trim());
  const isShared = platformList.length > 1;

  if (isShared) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
        <Share2 className="w-3 h-3" />
        Shared
      </span>
    );
  }

  const platform = platformList[0];
  if (platform === 'mobile') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
        <Smartphone className="w-3 h-3" />
        Mobile
      </span>
    );
  }
  if (platform === 'extension') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
        <Monitor className="w-3 h-3" />
        Extension
      </span>
    );
  }
  if (platform === 'web') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
        <Globe className="w-3 h-3" />
        Web
      </span>
    );
  }
  return null;
}

// App strings list view for a selected language
function AppStringsList({
  language,
  strings,
  onSelectItem,
  selectedStringId,
  platformFilter,
}: {
  language: string;
  strings: Array<{
    id: string;
    key: string;
    english_text: string;
    category: string | null;
    platforms?: string;
    translations: Record<string, { status: string; translated_text?: string }>;
  }>;
  onSelectItem: (stringId: string) => void;
  selectedStringId: string | null;
  platformFilter: 'all' | 'mobile' | 'extension' | 'web' | 'shared';
}) {
  const [filter, setFilter] = useState<'all' | 'missing' | 'translated'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredStrings = strings.filter((item) => {
    const t = item.translations[language];
    const matchesSearch =
      !searchTerm ||
      item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.english_text.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Platform filter
    const platforms = item.platforms || 'mobile';
    const platformList = platforms.split(',').map(p => p.trim());
    const isShared = platformList.length > 1;

    if (platformFilter !== 'all') {
      if (platformFilter === 'shared') {
        if (!isShared) return false;
      } else {
        if (!platformList.includes(platformFilter)) return false;
      }
    }

    const hasTranslation = t?.status === 'translated';

    switch (filter) {
      case 'missing':
        return !hasTranslation;
      case 'translated':
        return hasTranslation;
      default:
        return true;
    }
  });

  // Group by category
  const groupedStrings = filteredStrings.reduce((acc, item) => {
    const category = item.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, typeof filteredStrings>);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search strings..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'missing', label: 'Missing' },
            { id: 'translated', label: 'Done' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id as typeof filter)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === f.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* String list */}
      <div className="space-y-3">
        {Object.entries(groupedStrings).map(([category, items]) => (
          <div key={category} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700 capitalize">{category}</span>
              <span className="text-xs text-gray-500 ml-2">({items.length} items)</span>
            </div>
            <div className="divide-y divide-gray-100">
              {items.map((item) => {
                const t = item.translations[language];
                const isSelected = selectedStringId === item.id;
                const hasTranslation = t?.status === 'translated';
                const platforms = item.platforms || 'mobile';
                return (
                  <button
                    key={item.id}
                    onClick={() => onSelectItem(item.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">{item.key}</span>
                          <PlatformBadge platforms={platforms} />
                          {hasTranslation ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{item.english_text}</p>
                        {hasTranslation && t.translated_text && (
                          <p className="text-xs text-blue-600 truncate mt-1">{t.translated_text}</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {filteredStrings.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No strings match your filter
          </div>
        )}
      </div>
    </div>
  );
}

function AppStringsTab() {
  const queryClient = useQueryClient();
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [selectedStringId, setSelectedStringId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [platformFilter, setPlatformFilter] = useState<'all' | 'mobile' | 'extension' | 'web' | 'shared'>('all');

  // Frontend-driven translation state - persisted in localStorage
  const [appStringTranslation, setAppStringTranslation] = useState<AppStringTranslationState>(() => {
    try {
      const saved = localStorage.getItem('app-string-translation-progress');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...parsed, isRunning: false };
      }
    } catch (e) {
      console.error('Failed to restore app string translation progress:', e);
    }
    return {
      isRunning: false,
      items: [],
      currentIndex: 0,
      completed: 0,
      failed: 0,
      currentItem: null,
      lastError: null,
      startedAt: null,
      forceMode: false,
    };
  });

  // Save translation progress to localStorage
  useEffect(() => {
    if (appStringTranslation.items.length > 0) {
      localStorage.setItem('app-string-translation-progress', JSON.stringify(appStringTranslation));
    }
  }, [appStringTranslation]);

  // Check if there's a resumable translation
  const hasResumableTranslation = !appStringTranslation.isRunning &&
    appStringTranslation.items.length > 0 &&
    appStringTranslation.currentIndex < appStringTranslation.items.length;

  // Fetch translation status with language cards data
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-app-string-translation-status'],
    queryFn: () => api.getAppStringTranslationStatus(),
  });

  const languages = useMemo(() => data?.languages || [], [data?.languages]);
  const strings = useMemo(() => data?.strings || [], [data?.strings]);

  // Calculate stats per language
  const languageStats = useCallback(() => {
    const stats: Record<string, { translated: number; missing: number; total: number }> = {};

    languages.forEach((lang) => {
      let translated = 0;
      let missing = 0;

      strings.forEach((item) => {
        const t = item.translations[lang];
        if (t?.status === 'translated') {
          translated++;
        } else {
          missing++;
        }
      });

      stats[lang] = {
        translated,
        missing,
        total: strings.length,
      };
    });

    return stats;
  }, [languages, strings]);

  const stats = languageStats();

  // Overall stats
  const overallStats = useCallback(() => {
    let translated = 0;
    let missing = 0;
    const total = strings.length * languages.length;

    strings.forEach((item) => {
      languages.forEach((lang) => {
        const t = item.translations[lang];
        if (t?.status === 'translated') {
          translated++;
        } else {
          missing++;
        }
      });
    });

    return { translated, missing, total };
  }, [strings, languages]);

  const overall = overallStats();

  // Reset all app string translations
  const resetAppStringTranslationsMutation = useMutation({
    mutationFn: () => api.resetAppStringTranslations(),
    onSuccess: (result) => {
      setStatusMessage({
        type: 'success',
        text: result.message || 'All app string translations deleted',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-app-string-translations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-app-string-translation-status'] });
      refetch();
      setTimeout(() => setStatusMessage(null), 5000);
    },
    onError: (error: Error) => {
      setStatusMessage({
        type: 'error',
        text: error.message || 'Failed to reset app string translations',
      });
      setTimeout(() => setStatusMessage(null), 5000);
    },
  });

  // Start frontend-driven translation
  const startAppStringTranslation = async (force: boolean = false) => {
    try {
      const result = await api.getAppStringsToTranslate(force);

      if (!result.items.length) {
        const alreadyComplete = result.already_complete || 0;
        if (alreadyComplete > 0 && !force) {
          setStatusMessage({
            type: 'success',
            text: `All ${alreadyComplete} app strings are already fully translated!`,
          });
        } else {
          setStatusMessage({ type: 'error', text: 'No app strings to translate' });
        }
        return;
      }

      if (force) {
        console.log(`[AppStrings] FORCE MODE: translating all ${result.items.length} strings`);
      } else {
        console.log(`[AppStrings] Translating ${result.items.length} strings`);
      }

      setAppStringTranslation({
        isRunning: true,
        items: result.items.map(i => ({ id: i.id, key: i.key, category: i.category })),
        currentIndex: 0,
        completed: 0,
        failed: 0,
        currentItem: result.items[0].key,
        lastError: null,
        startedAt: Date.now(),
        forceMode: force,
      });
    } catch (error: unknown) {
      setStatusMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to start translation' });
    }
  };

  // Resume paused translation
  const resumeAppStringTranslation = () => {
    if (!hasResumableTranslation) return;
    setAppStringTranslation(prev => ({
      ...prev,
      isRunning: true,
      startedAt: Date.now(),
    }));
  };

  // Clear saved progress
  const clearSavedProgress = () => {
    localStorage.removeItem('app-string-translation-progress');
    setAppStringTranslation({
      isRunning: false,
      items: [],
      currentIndex: 0,
      completed: 0,
      failed: 0,
      currentItem: null,
      lastError: null,
      startedAt: null,
      forceMode: false,
    });
  };

  // Process translation items one by one
  useEffect(() => {
    if (!appStringTranslation.isRunning) return;
    if (appStringTranslation.currentIndex >= appStringTranslation.items.length) {
      // All done - clear saved progress
      localStorage.removeItem('app-string-translation-progress');
      setAppStringTranslation(prev => ({ ...prev, isRunning: false, currentItem: null, items: [] }));
      setStatusMessage({
        type: 'success',
        text: `Translation complete: ${appStringTranslation.completed} succeeded, ${appStringTranslation.failed} failed`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin-app-string-translation-status'] });
      refetch();
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }

    const processNextItem = async () => {
      const item = appStringTranslation.items[appStringTranslation.currentIndex];

      setAppStringTranslation(prev => ({
        ...prev,
        currentItem: item.key,
      }));

      try {
        await api.translateAppStringToAllLanguages(item.id, appStringTranslation.forceMode);
        setAppStringTranslation(prev => ({
          ...prev,
          completed: prev.completed + 1,
          currentIndex: prev.currentIndex + 1,
          lastError: null,
        }));
        // Refresh status after each item
        refetch();
      } catch (error: unknown) {
        setAppStringTranslation(prev => ({
          ...prev,
          failed: prev.failed + 1,
          currentIndex: prev.currentIndex + 1,
          lastError: error instanceof Error ? error.message : 'Unknown error',
        }));
      }
    };

    processNextItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only depends on isRunning/currentIndex to process items sequentially
  }, [appStringTranslation.isRunning, appStringTranslation.currentIndex, appStringTranslation.forceMode]);

  // Stop translation
  const stopAppStringTranslation = () => {
    setAppStringTranslation(prev => ({
      ...prev,
      isRunning: false,
      currentItem: null,
    }));
    setStatusMessage({ type: 'success', text: 'Translation stopped' });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  return (
    <div className="space-y-6">
      {/* Status Message */}
      {statusMessage && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            statusMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {statusMessage.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          {statusMessage.text}
        </div>
      )}

      {/* Resume Translation Banner */}
      {hasResumableTranslation && !appStringTranslation.isRunning && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <div>
                <span className="font-medium text-gray-900">Translation paused</span>
                <span className="text-sm text-gray-600 ml-2">
                  {appStringTranslation.currentIndex} of {appStringTranslation.items.length} strings completed
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSavedProgress}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Discard
              </button>
              <button
                onClick={resumeAppStringTranslation}
                className="px-4 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
              >
                Resume Translation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Frontend-driven Translation Progress Bar */}
      {appStringTranslation.isRunning && (
        <div className={`bg-white rounded-lg border p-4 shadow-sm ${appStringTranslation.forceMode ? 'border-purple-200' : 'border-green-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className={`w-5 h-5 animate-spin ${appStringTranslation.forceMode ? 'text-purple-600' : 'text-green-600'}`} />
              <span className="font-medium text-gray-900">
                {appStringTranslation.forceMode ? 'Force translating all strings...' : 'Translating missing strings...'}
              </span>
              {appStringTranslation.forceMode && (
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">FORCE</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-green-600">
                {appStringTranslation.items.length > 0
                  ? Math.round((appStringTranslation.currentIndex / appStringTranslation.items.length) * 100)
                  : 0}%
              </span>
              <button
                onClick={stopAppStringTranslation}
                className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                Stop
              </button>
            </div>
          </div>

          {/* Current item being processed */}
          {appStringTranslation.currentItem && (
            <div className="mb-2 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-green-700 font-medium">
                  Processing: <code className="bg-green-100 px-1.5 py-0.5 rounded">{appStringTranslation.currentItem}</code>
                </span>
              </div>
            </div>
          )}

          {/* Error display */}
          {appStringTranslation.lastError && (
            <div className="mb-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700 font-medium">
                  Last Error: <code className="bg-red-100 px-1.5 py-0.5 rounded">{appStringTranslation.lastError}</code>
                </span>
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div
              className="h-full transition-all duration-300 ease-out bg-green-500"
              style={{ width: `${appStringTranslation.items.length > 0 ? (appStringTranslation.currentIndex / appStringTranslation.items.length) * 100 : 0}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {appStringTranslation.currentIndex} of {appStringTranslation.items.length} strings
              {appStringTranslation.failed > 0 && (
                <span className="text-red-500 ml-2">({appStringTranslation.failed} failed)</span>
              )}
            </span>
          </div>

          {/* Time stats */}
          {appStringTranslation.startedAt && (
            <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>
                Elapsed: {formatDuration(Date.now() - appStringTranslation.startedAt)}
              </span>
              {appStringTranslation.currentIndex > 0 && (
                <span>
                  ~{Math.round((Date.now() - appStringTranslation.startedAt) / appStringTranslation.currentIndex / 1000)}s per string
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {selectedLanguage && (
            <button
              onClick={() => {
                setSelectedLanguage(null);
                setSelectedStringId(null);
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronUp className="w-4 h-4 rotate-[-90deg]" />
              Back to all languages
            </button>
          )}
          {/* Platform filter dropdown */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value as typeof platformFilter)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Platforms</option>
              <option value="shared">Shared Only</option>
              <option value="mobile">Mobile Only</option>
              <option value="extension">Extension Only</option>
              <option value="web">Web Only</option>
            </select>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={async () => {
              try {
                const result = await api.syncSharedStringsBidirectional();
                setStatusMessage({ type: 'success', text: result.message });
                refetch();
              } catch (error: unknown) {
                setStatusMessage({ type: 'error', text: error instanceof Error ? error.message : 'Sync failed' });
              }
              setTimeout(() => setStatusMessage(null), 5000);
            }}
            disabled={appStringTranslation.isRunning}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Sync translations between Website Content and shared app strings (bidirectional)"
          >
            <Share2 className="w-4 h-4" />
            Sync Shared Strings
          </button>
          <button
            onClick={() => startAppStringTranslation(false)}
            disabled={appStringTranslation.isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Languages className="w-4 h-4" />
            {appStringTranslation.isRunning ? 'Translation in Progress...' : 'Translate Missing'}
          </button>
          <button
            onClick={() => {
              if (confirm('Force translate will RE-TRANSLATE all strings, even those already done. This will use more API credits. Continue?')) {
                startAppStringTranslation(true);
              }
            }}
            disabled={appStringTranslation.isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            Force Translate All
          </button>
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete ALL app string translations? This cannot be undone.')) {
                resetAppStringTranslationsMutation.mutate();
              }
            }}
            disabled={resetAppStringTranslationsMutation.isPending || appStringTranslation.isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {resetAppStringTranslationsMutation.isPending ? 'Resetting...' : 'Reset All'}
          </button>
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{languages.length}</p>
          <p className="text-sm text-gray-500">Languages</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{strings.length}</p>
          <p className="text-sm text-gray-500">UI Strings</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-green-600">{overall.translated}</p>
          <p className="text-sm text-gray-500">Translated</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-400">{overall.missing}</p>
          <p className="text-sm text-gray-500">Missing</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-primary-600">
            {overall.total > 0 ? Math.round((overall.translated / overall.total) * 100) : 0}%
          </p>
          <p className="text-sm text-gray-500">Complete</p>
        </div>
      </div>

      {/* Platform Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Filter by platform:</span>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'all' as const, label: 'All', icon: null },
            { id: 'web' as const, label: 'Web', icon: Globe },
            { id: 'mobile' as const, label: 'Mobile', icon: Smartphone },
            { id: 'extension' as const, label: 'Extension', icon: Monitor },
            { id: 'shared' as const, label: 'Shared', icon: Share2 },
          ].map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                onClick={() => setPlatformFilter(p.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  platformFilter === p.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {Icon && <Icon className="w-4 h-4" />}
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      ) : selectedLanguage ? (
        /* Language-specific strings view */
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {LANGUAGE_NAMES[selectedLanguage] || selectedLanguage} Translations
              </h2>
              <p className="text-sm text-gray-500">
                {stats[selectedLanguage]?.translated || 0} of {strings.length} translated
              </p>
            </div>
            <AppStringsList
              language={selectedLanguage}
              strings={strings}
              onSelectItem={setSelectedStringId}
              selectedStringId={selectedStringId}
              platformFilter={platformFilter}
            />
          </div>
        </div>
      ) : (
        /* Language cards grid */
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Translation Status by Language</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {languages.map((lang) => (
              <AppStringLanguageCard
                key={lang}
                language={lang}
                stats={stats[lang] || { translated: 0, missing: 0, total: 0 }}
                isSelected={selectedLanguage === lang}
                onClick={() => setSelectedLanguage(lang)}
              />
            ))}
          </div>

          {languages.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No languages configured. Click "Translate Missing" to start.
            </div>
          )}

          {languages.length > 0 && strings.length === 0 && (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg mt-4">
              No app strings defined yet. Add strings to the content_strings table to begin translation.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// WEBSITE CONTENT TAB COMPONENTS
// ============================================

function StatusIcon({ status, score }: { status?: string; score?: number | null }) {
  if (!status) {
    return (
      <span title="Pending">
        <Clock className="w-4 h-4 text-gray-400" />
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <span title="Failed">
        <X className="w-4 h-4 text-red-500" />
      </span>
    );
  }

  if (status === 'reviewed') {
    return (
      <span title="Reviewed">
        <Check className="w-4 h-4 text-blue-500" />
      </span>
    );
  }

  if (score !== null && score !== undefined && score < 0.7) {
    return (
      <span title={`Low quality: ${Math.round(score * 10)}/10`}>
        <AlertTriangle className="w-4 h-4 text-amber-500" />
      </span>
    );
  }

  return (
    <span title="Translated">
      <Check className="w-4 h-4 text-green-500" />
    </span>
  );
}

function TranslationDetail({
  contentId,
  language,
  onClose,
}: {
  contentId: string;
  language: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [editedText, setEditedText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'ai'; content: string }>>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-translation-detail', contentId, language],
    queryFn: () => api.getAdminTranslation(contentId, language),
    enabled: !!contentId && !!language,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => api.analyzeAdminTranslation(contentId, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-translation-detail', contentId, language] });
      queryClient.invalidateQueries({ queryKey: ['admin-translation-status'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (text: string) => api.updateAdminTranslation(contentId, language, text, 'admin'),
    onSuccess: () => {
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['admin-translation-detail', contentId, language] });
      queryClient.invalidateQueries({ queryKey: ['admin-translation-status'] });
    },
  });

  const chatMutation = useMutation({
    mutationFn: (question: string) =>
      api.chatAboutTranslation(question, {
        language,
        english_text: data?.content?.english_value,
        translated_text: data?.translation?.translated_text,
      }),
    onSuccess: (response, question) => {
      setChatMessages((prev) => [
        ...prev,
        { role: 'user', content: question },
        { role: 'ai', content: response.answer },
      ]);
      setChatQuestion('');
    },
  });

  const handleSendChat = () => {
    if (chatQuestion.trim()) {
      chatMutation.mutate(chatQuestion.trim());
    }
  };

  const handleStartEdit = () => {
    setEditedText(data?.translation?.translated_text || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editedText.trim()) {
      updateMutation.mutate(editedText.trim());
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-lg">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Languages className="w-5 h-5 text-primary-600" />
          <div>
            <h3 className="font-semibold text-gray-900">
              {data?.content?.section}.{data?.content?.key}
            </h3>
            <p className="text-sm text-gray-500">{LANGUAGE_NAMES[language] || language}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">English (Source)</span>
          <div className="p-3 bg-gray-50 rounded-lg text-gray-900">{data?.content?.english_value}</div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="translation-textarea" className="block text-sm font-medium text-gray-700">
              {LANGUAGE_NAMES[language] || language} Translation
            </label>
            {data?.translation?.quality_score !== null && data?.translation?.quality_score !== undefined && (
              <span
                className={`text-sm px-2 py-0.5 rounded ${
                  data.translation.quality_score >= 0.8
                    ? 'bg-green-100 text-green-700'
                    : data.translation.quality_score >= 0.6
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                Score: {Math.round(data.translation.quality_score * 10)}/10
              </span>
            )}
          </div>
          {data?.translation ? (
            isEditing ? (
              <div className="space-y-2">
                <textarea
                  id="translation-textarea"
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows={3}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-blue-50 rounded-lg text-gray-900">{data.translation.translated_text}</div>
            )
          ) : (
            <div className="p-3 bg-gray-50 rounded-lg text-gray-500 italic">No translation yet</div>
          )}
        </div>

        {data?.translation?.quality_issues && data.translation.quality_issues.length > 0 && (
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-medium text-amber-800 mb-2">Quality Issues</h4>
            <ul className="space-y-1">
              {data.translation.quality_issues.map((issue, i) => (
                <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong className="capitalize">{issue.type}:</strong> {issue.description}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {!isEditing && (
            <button
              onClick={handleStartEdit}
              className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Edit Translation
            </button>
          )}
          <button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending || !data?.translation}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
          >
            <Sparkles className="w-4 h-4" />
            {analyzeMutation.isPending ? 'Analyzing...' : 'Analyze Quality'}
          </button>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Ask AI About This Translation
          </h4>

          {chatMessages.length > 0 && (
            <div className="space-y-3 mb-3 max-h-60 overflow-y-auto">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg ${
                    msg.role === 'user' ? 'bg-gray-100 ml-8' : 'bg-blue-50 mr-8'
                  }`}
                >
                  <p className="text-xs text-gray-500 mb-1">{msg.role === 'user' ? 'You' : 'AI'}</p>
                  <p className="text-sm text-gray-900">{msg.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Pre-built prompts */}
          <div className="flex flex-wrap gap-1.5 mb-3">
            {TRANSLATION_PROMPTS.map((p) => (
              <button
                key={p.label}
                type="button"
                onClick={() => setChatQuestion(p.prompt)}
                className={`px-2 py-1 text-xs rounded-full transition-colors ${PROMPT_CATEGORY_COLORS[p.category]}`}
                title={p.prompt}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              value={chatQuestion}
              onChange={(e) => setChatQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendChat()}
              placeholder="Ask about this translation..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />
            <button
              onClick={handleSendChat}
              disabled={chatMutation.isPending || !chatQuestion.trim()}
              className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {chatMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Language card component showing completion stats for a single language
function LanguageCard({
  language,
  stats,
  isSelected,
  onClick,
}: {
  language: string;
  stats: { translated: number; lowQuality: number; missing: number; total: number };
  isSelected: boolean;
  onClick: () => void;
}) {
  const percentage = stats.total > 0 ? Math.round((stats.translated / stats.total) * 100) : 0;
  const hasIssues = stats.lowQuality > 0 || stats.missing > 0;

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-lg border text-left transition-all hover:shadow-md ${
        isSelected
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
          : hasIssues
          ? 'border-amber-200 bg-white hover:border-amber-300'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-900">{LANGUAGE_NAMES[language] || language}</span>
        <span className="text-xs font-medium text-gray-500 uppercase">{language}</span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full transition-all ${
            percentage === 100 ? 'bg-green-500' : percentage > 50 ? 'bg-primary-500' : 'bg-amber-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className={percentage === 100 ? 'text-green-600 font-medium' : 'text-gray-600'}>
          {percentage}% complete
        </span>
        <div className="flex items-center gap-2">
          {stats.missing > 0 && (
            <span className="flex items-center gap-0.5 text-gray-400">
              <Clock className="w-3 h-3" />
              {stats.missing}
            </span>
          )}
          {stats.lowQuality > 0 && (
            <span className="flex items-center gap-0.5 text-amber-500">
              <AlertTriangle className="w-3 h-3" />
              {stats.lowQuality}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

// Content list view for a selected language
function LanguageContentList({
  language,
  content,
  onSelectItem,
  selectedContentId,
}: {
  language: string;
  content: Array<{
    content_id: string;
    section: string;
    key: string;
    english_value: string;
    translations: Record<string, { status: string; quality_score: number | null }>;
  }>;
  onSelectItem: (contentId: string) => void;
  selectedContentId: string | null;
}) {
  const [filter, setFilter] = useState<'all' | 'missing' | 'low-quality' | 'translated'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredContent = content.filter((item) => {
    const t = item.translations[language];
    const matchesSearch =
      !searchTerm ||
      item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.english_value.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Use status to determine translation state
    const hasTranslation = !!t?.status;
    const isLowQuality = t?.quality_score !== null && t?.quality_score !== undefined && t.quality_score < 0.7;

    switch (filter) {
      case 'missing':
        return !hasTranslation;
      case 'low-quality':
        return hasTranslation && isLowQuality;
      case 'translated':
        return hasTranslation;
      default:
        return true;
    }
  });

  // Group by section
  const groupedContent = filteredContent.reduce((acc, item) => {
    if (!acc[item.section]) {
      acc[item.section] = [];
    }
    acc[item.section].push(item);
    return acc;
  }, {} as Record<string, typeof filteredContent>);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search content..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'missing', label: 'Missing' },
            { id: 'low-quality', label: 'Low Quality' },
            { id: 'translated', label: 'Done' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id as typeof filter)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                filter === f.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content list */}
      <div className="space-y-3">
        {Object.entries(groupedContent).map(([section, items]) => (
          <div key={section} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-700 capitalize">{section}</span>
              <span className="text-xs text-gray-500 ml-2">({items.length} items)</span>
            </div>
            <div className="divide-y divide-gray-100">
              {items.map((item) => {
                const t = item.translations[language];
                const isSelected = selectedContentId === item.content_id;
                return (
                  <button
                    key={item.content_id}
                    onClick={() => onSelectItem(item.content_id)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{item.key}</span>
                          <StatusIcon status={t?.status} score={t?.quality_score} />
                        </div>
                        <p className="text-xs text-gray-500 truncate">{item.english_value}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {filteredContent.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No content matches your filter
          </div>
        )}
      </div>
    </div>
  );
}

// Frontend-driven translation state
interface FrontendTranslationState {
  isRunning: boolean;
  items: Array<{ id: string; section: string; key: string }>;
  currentIndex: number;
  completed: number;
  failed: number;
  currentItem: string | null;
  lastError: string | null;
  startedAt: number | null;
  forceMode: boolean;
}

function WebsiteContentTab() {
  const queryClient = useQueryClient();
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null);
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Frontend-driven translation state - persisted in localStorage
  const [frontendTranslation, setFrontendTranslation] = useState<FrontendTranslationState>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem('translation-progress');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Don't auto-resume, just restore the state for display
        return { ...parsed, isRunning: false };
      }
    } catch (e) {
      console.error('Failed to restore translation progress:', e);
    }
    return {
      isRunning: false,
      items: [],
      currentIndex: 0,
      completed: 0,
      failed: 0,
      currentItem: null,
      lastError: null,
      startedAt: null,
      forceMode: false,
    };
  });

  // Save translation progress to localStorage whenever it changes
  useEffect(() => {
    if (frontendTranslation.items.length > 0) {
      localStorage.setItem('translation-progress', JSON.stringify(frontendTranslation));
    }
  }, [frontendTranslation]);

  // Check if there's a resumable translation
  const hasResumableTranslation = !frontendTranslation.isRunning &&
    frontendTranslation.items.length > 0 &&
    frontendTranslation.currentIndex < frontendTranslation.items.length;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-translation-status'],
    queryFn: () => api.getAdminTranslationStatus(),
    // Refetch every 3 seconds while job is running to show real-time progress
    refetchInterval: activeJobId ? 3000 : false,
  });

  // Poll for job status when there's an active job
  const { data: jobData } = useQuery({
    queryKey: ['translation-job', activeJobId],
    queryFn: () => activeJobId ? api.getTranslationJobStatus(activeJobId) : null,
    enabled: !!activeJobId,
    refetchInterval: activeJobId ? 2000 : false, // Poll every 2 seconds while job is active
  });

  // Check for existing running job - poll until we find one or have an active job
  const { data: latestJobData } = useQuery({
    queryKey: ['latest-translation-job'],
    queryFn: () => api.getLatestTranslationJob(),
    refetchInterval: activeJobId ? false : 5000, // Keep checking every 5s if no active job
  });

  // Restore activeJobId from latest job if it's running
  useEffect(() => {
    if (latestJobData?.job?.status === 'running' && !activeJobId) {
      console.log('[Translations] Restoring running job:', latestJobData.job.id);
      setActiveJobId(latestJobData.job.id);
    }
  }, [latestJobData, activeJobId]);

  // When job completes, clear it and refresh data
  useEffect(() => {
    if (jobData?.status === 'completed' || jobData?.status === 'failed' || jobData?.status === 'cancelled') {
      // Refresh translation data
      queryClient.invalidateQueries({ queryKey: ['admin-translation-status'] });
      // Show completion message
      if (jobData.status === 'completed') {
        setStatusMessage({
          type: 'success',
          text: `Translation completed! ${jobData.completed_items}/${jobData.total_items} items translated${jobData.failed_items > 0 ? ` (${jobData.failed_items} failed)` : ''}.`,
        });
      } else if (jobData.status === 'cancelled') {
        setStatusMessage({
          type: 'success',
          text: `Translation cancelled. ${jobData.completed_items}/${jobData.total_items} items were translated before cancellation.`,
        });
      } else {
        setStatusMessage({
          type: 'error',
          text: jobData.error_message || 'Translation job failed',
        });
      }
      // Clear job after a short delay
      setTimeout(() => {
        setActiveJobId(null);
        setStatusMessage(null);
      }, 5000);
    }
  }, [jobData?.status, jobData?.completed_items, jobData?.total_items, jobData?.failed_items, jobData?.error_message, queryClient]);

  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => api.cancelTranslationJob(jobId),
    onSuccess: () => {
      // Job will be updated via polling, no need to do anything here
    },
    onError: (error: Error) => {
      setStatusMessage({
        type: 'error',
        text: error.message || 'Failed to cancel translation',
      });
    },
  });

  const cancelAllJobsMutation = useMutation({
    mutationFn: () => api.cancelAllTranslationJobs(),
    onSuccess: (result) => {
      setActiveJobId(null);
      setStatusMessage({
        type: 'success',
        text: result.message || 'All jobs cancelled',
      });
      queryClient.invalidateQueries({ queryKey: ['translation-job'] });
      setTimeout(() => setStatusMessage(null), 5000);
    },
    onError: (error: Error) => {
      setStatusMessage({
        type: 'error',
        text: error.message || 'Failed to cancel jobs',
      });
      setTimeout(() => setStatusMessage(null), 5000);
    },
  });

  const resetAllTranslationsMutation = useMutation({
    mutationFn: () => api.resetAllTranslations(),
    onSuccess: (result) => {
      setActiveJobId(null);
      setStatusMessage({
        type: 'success',
        text: result.message || 'All translations deleted',
      });
      queryClient.invalidateQueries({ queryKey: ['admin-website-translations'] });
      queryClient.invalidateQueries({ queryKey: ['translation-job'] });
      refetch();
      setTimeout(() => setStatusMessage(null), 5000);
    },
    onError: (error: Error) => {
      setStatusMessage({
        type: 'error',
        text: error.message || 'Failed to reset translations',
      });
      setTimeout(() => setStatusMessage(null), 5000);
    },
  });

  // Frontend-driven translation: start the process
  // force=true will re-translate ALL items regardless of cache
  const startFrontendTranslation = async (force: boolean = false) => {
    try {
      // First, cancel any running backend jobs
      await api.cancelAllTranslationJobs();

      // Get list of items that NEED translation (already filtered on server)
      // If force=true, server returns ALL items
      const result = await api.getItemsToTranslate(force);

      // Check if everything is already done
      if (!result.items.length) {
        const alreadyComplete = result.already_complete || 0;
        if (alreadyComplete > 0 && !force) {
          setStatusMessage({
            type: 'success',
            text: `All ${alreadyComplete} content items are already fully translated!`,
          });
        } else {
          setStatusMessage({ type: 'error', text: 'No content items to translate' });
        }
        return;
      }

      // Show info about what needs translation
      const alreadyComplete = result.already_complete || 0;
      if (alreadyComplete > 0) {
        console.log(`[Translations] ${alreadyComplete} items already complete, ${result.items.length} need translation`);
      }
      if (force) {
        console.log(`[Translations] FORCE MODE: translating all ${result.items.length} items`);
      }

      setFrontendTranslation({
        isRunning: true,
        items: result.items.map(i => ({ id: i.id, section: i.section, key: i.key })),
        currentIndex: 0,
        completed: 0,
        failed: 0,
        currentItem: `${result.items[0].section}.${result.items[0].key}`,
        lastError: null,
        startedAt: Date.now(),
        forceMode: force,
      });
    } catch (error: unknown) {
      setStatusMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to start translation' });
    }
  };

  // Resume a paused translation
  const resumeFrontendTranslation = () => {
    if (!hasResumableTranslation) return;
    setFrontendTranslation(prev => ({
      ...prev,
      isRunning: true,
      startedAt: Date.now(),
    }));
  };

  // Clear saved progress
  const clearSavedProgress = () => {
    localStorage.removeItem('translation-progress');
    setFrontendTranslation({
      isRunning: false,
      items: [],
      currentIndex: 0,
      completed: 0,
      failed: 0,
      currentItem: null,
      lastError: null,
      startedAt: null,
      forceMode: false,
    });
  };

  // Frontend-driven translation: process items one by one
  useEffect(() => {
    if (!frontendTranslation.isRunning) return;
    if (frontendTranslation.currentIndex >= frontendTranslation.items.length) {
      // All done - clear saved progress
      localStorage.removeItem('translation-progress');
      setFrontendTranslation(prev => ({ ...prev, isRunning: false, currentItem: null, items: [] }));
      setStatusMessage({
        type: 'success',
        text: `Translation complete: ${frontendTranslation.completed} succeeded, ${frontendTranslation.failed} failed`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin-translation-status'] });
      refetch();
      setTimeout(() => setStatusMessage(null), 5000);
      return;
    }

    const processNextItem = async () => {
      const item = frontendTranslation.items[frontendTranslation.currentIndex];
      const itemName = `${item.section}.${item.key}`;

      setFrontendTranslation(prev => ({
        ...prev,
        currentItem: itemName,
      }));

      try {
        // Use force mode if set
        await api.translateSingleItem(item.id, frontendTranslation.forceMode);
        setFrontendTranslation(prev => ({
          ...prev,
          completed: prev.completed + 1,
          currentIndex: prev.currentIndex + 1,
          lastError: null,
        }));
        // Refresh status after each item
        refetch();
      } catch (error: unknown) {
        setFrontendTranslation(prev => ({
          ...prev,
          failed: prev.failed + 1,
          currentIndex: prev.currentIndex + 1,
          lastError: error instanceof Error ? error.message : 'Unknown error',
        }));
      }
    };

    processNextItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally only depends on isRunning/currentIndex to process items sequentially
  }, [frontendTranslation.isRunning, frontendTranslation.currentIndex, frontendTranslation.forceMode]);

  // Stop frontend translation
  const stopFrontendTranslation = () => {
    setFrontendTranslation(prev => ({
      ...prev,
      isRunning: false,
      currentItem: null,
      forceMode: false,
    }));
    setStatusMessage({ type: 'success', text: 'Translation stopped' });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  const languages = useMemo(() => data?.languages || [], [data?.languages]);
  const content = useMemo(() => data?.content || [], [data?.content]);

  // Calculate stats per language - use status to determine if translated
  const languageStats = useCallback(() => {
    const stats: Record<string, { translated: number; lowQuality: number; missing: number; total: number }> = {};

    languages.forEach((lang) => {
      let translated = 0;
      let lowQuality = 0;
      let missing = 0;

      content.forEach((item) => {
        const t = item.translations[lang];
        if (!t?.status) {
          missing++;
        } else if (t.quality_score !== null && t.quality_score !== undefined && t.quality_score < 0.7) {
          lowQuality++;
        } else {
          translated++;
        }
      });

      stats[lang] = {
        translated,
        lowQuality,
        missing,
        total: content.length,
      };
    });

    return stats;
  }, [languages, content]);

  const stats = languageStats();

  // Overall stats - use status to determine if translated
  const overallStats = useCallback(() => {
    let translated = 0;
    let lowQuality = 0;
    let missing = 0;
    const total = content.length * languages.length;

    content.forEach((item) => {
      languages.forEach((lang) => {
        const t = item.translations[lang];
        if (!t?.status) {
          missing++;
        } else if (t.quality_score !== null && t.quality_score !== undefined && t.quality_score < 0.7) {
          lowQuality++;
        } else {
          translated++;
        }
      });
    });

    return { translated, lowQuality, missing, total };
  }, [content, languages]);

  const overall = overallStats();

  return (
    <div className="space-y-6">
      {/* Status Message */}
      {statusMessage && !activeJobId && (
        <div
          className={`p-4 rounded-lg flex items-center gap-2 ${
            statusMessage.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {statusMessage.type === 'success' ? (
            <Check className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          {statusMessage.text}
        </div>
      )}

      {/* Translation Job Progress Bar */}
      {activeJobId && jobData && (
        <div className="bg-white rounded-lg border border-primary-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {jobData.status === 'running' ? (
                <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
              ) : (
                <Check className="w-5 h-5 text-green-600" />
              )}
              <span className="font-medium text-gray-900">
                {jobData.status === 'running' ? 'Translating content...' :
                 jobData.status === 'cancelled' ? 'Translation cancelled' : 'Translation complete'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-primary-600">
                {jobData.progress}%
              </span>
              {jobData.status === 'running' && (
                <button
                  onClick={() => cancelJobMutation.mutate(activeJobId)}
                  disabled={cancelJobMutation.isPending}
                  className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {cancelJobMutation.isPending ? 'Cancelling...' : 'Cancel'}
                </button>
              )}
            </div>
          </div>

          {/* Current item being processed */}
          {jobData.status === 'running' && jobData.current_item && (
            <div className="mb-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-sm text-blue-700 font-medium">
                  Processing: <code className="bg-blue-100 px-1.5 py-0.5 rounded">{jobData.current_item}</code>
                </span>
                {jobData.item_started_at && (
                  <span className="text-xs text-blue-500 ml-2">
                    (started {new Date(jobData.item_started_at).toLocaleTimeString()})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Error display */}
          {(jobData.last_error || jobData.fatal_error) && (
            <div className="mb-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700 font-medium">
                  {jobData.fatal_error ? 'Fatal Error: ' : 'Last Error: '}
                  <code className="bg-red-100 px-1.5 py-0.5 rounded">{jobData.fatal_error || jobData.last_error}</code>
                </span>
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full transition-all duration-300 ease-out ${
                jobData.status === 'cancelled' ? 'bg-amber-500' : 'bg-primary-500'
              }`}
              style={{ width: `${jobData.progress}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {jobData.completed_items} of {jobData.total_items} content items
              {jobData.failed_items > 0 && (
                <span className="text-red-500 ml-2">({jobData.failed_items} failed)</span>
              )}
            </span>
            <span className="text-gray-500">
              {jobData.total_languages} languages per item
            </span>
          </div>

          {/* Time stats */}
          {jobData.status === 'running' && jobData.elapsed_ms > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>
                Elapsed: {formatDuration(jobData.elapsed_ms)}
              </span>
              {jobData.estimated_remaining_ms > 0 && jobData.completed_items > 0 && (
                <span>
                  Est. remaining: {formatDuration(jobData.estimated_remaining_ms)}
                </span>
              )}
              {jobData.completed_items > 0 && (
                <span>
                  ~{Math.round(jobData.elapsed_ms / jobData.completed_items / 1000)}s per item
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Resume Translation Banner */}
      {hasResumableTranslation && !frontendTranslation.isRunning && (
        <div className="bg-amber-50 rounded-lg border border-amber-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <div>
                <span className="font-medium text-gray-900">Translation paused</span>
                <span className="text-sm text-gray-600 ml-2">
                  {frontendTranslation.currentIndex} of {frontendTranslation.items.length} items completed
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearSavedProgress}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Discard
              </button>
              <button
                onClick={resumeFrontendTranslation}
                className="px-4 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
              >
                Resume Translation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Frontend-driven Translation Progress Bar */}
      {frontendTranslation.isRunning && (
        <div className={`bg-white rounded-lg border p-4 shadow-sm ${frontendTranslation.forceMode ? 'border-purple-200' : 'border-green-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Loader2 className={`w-5 h-5 animate-spin ${frontendTranslation.forceMode ? 'text-purple-600' : 'text-green-600'}`} />
              <span className="font-medium text-gray-900">
                {frontendTranslation.forceMode ? 'Force translating all content...' : 'Translating missing content...'}
              </span>
              {frontendTranslation.forceMode && (
                <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">FORCE</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-green-600">
                {frontendTranslation.items.length > 0
                  ? Math.round((frontendTranslation.currentIndex / frontendTranslation.items.length) * 100)
                  : 0}%
              </span>
              <button
                onClick={stopFrontendTranslation}
                className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                Stop
              </button>
            </div>
          </div>

          {/* Current item being processed */}
          {frontendTranslation.currentItem && (
            <div className="mb-2 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm text-green-700 font-medium">
                  Processing: <code className="bg-green-100 px-1.5 py-0.5 rounded">{frontendTranslation.currentItem}</code>
                </span>
              </div>
            </div>
          )}

          {/* Error display */}
          {frontendTranslation.lastError && (
            <div className="mb-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-700 font-medium">
                  Last Error: <code className="bg-red-100 px-1.5 py-0.5 rounded">{frontendTranslation.lastError}</code>
                </span>
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-2">
            <div
              className="h-full transition-all duration-300 ease-out bg-green-500"
              style={{ width: `${frontendTranslation.items.length > 0 ? (frontendTranslation.currentIndex / frontendTranslation.items.length) * 100 : 0}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {frontendTranslation.currentIndex} of {frontendTranslation.items.length} content items
              {frontendTranslation.failed > 0 && (
                <span className="text-red-500 ml-2">({frontendTranslation.failed} failed)</span>
              )}
            </span>
          </div>

          {/* Time stats */}
          {frontendTranslation.startedAt && (
            <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>
                Elapsed: {formatDuration(Date.now() - frontendTranslation.startedAt)}
              </span>
              {frontendTranslation.currentIndex > 0 && (
                <span>
                  ~{Math.round((Date.now() - frontendTranslation.startedAt) / frontendTranslation.currentIndex / 1000)}s per item
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Header actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {selectedLanguage && (
            <button
              onClick={() => {
                setSelectedLanguage(null);
                setSelectedContentId(null);
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
            >
              <ChevronUp className="w-4 h-4 rotate-[-90deg]" />
              Back to all languages
            </button>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={async () => {
              try {
                const result = await api.syncSharedStringsBidirectional();
                setStatusMessage({ type: 'success', text: result.message });
                refetch();
              } catch (error: unknown) {
                setStatusMessage({ type: 'error', text: error instanceof Error ? error.message : 'Sync failed' });
              }
              setTimeout(() => setStatusMessage(null), 5000);
            }}
            disabled={frontendTranslation.isRunning || !!activeJobId}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Sync translations between Website Content and shared app strings (bidirectional)"
          >
            <Share2 className="w-4 h-4" />
            Sync Shared Strings
          </button>
          {activeJobId && (
            <button
              onClick={() => cancelAllJobsMutation.mutate()}
              disabled={cancelAllJobsMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              <StopCircle className="w-4 h-4" />
              {cancelAllJobsMutation.isPending ? 'Stopping...' : 'Kill Job'}
            </button>
          )}
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete ALL translations? This cannot be undone.')) {
                resetAllTranslationsMutation.mutate();
              }
            }}
            disabled={resetAllTranslationsMutation.isPending || !!activeJobId || frontendTranslation.isRunning}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            {resetAllTranslationsMutation.isPending ? 'Resetting...' : 'Reset All'}
          </button>
          <button
            onClick={() => startFrontendTranslation(false)}
            disabled={frontendTranslation.isRunning || !!activeJobId}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            <Languages className="w-4 h-4" />
            {frontendTranslation.isRunning ? 'Translation in Progress...' : activeJobId ? 'Backend Job Running...' : 'Translate Missing'}
          </button>
          <button
            onClick={() => {
              if (confirm('Force translate will RE-TRANSLATE all items, even those already done. This will use more API credits. Continue?')) {
                startFrontendTranslation(true);
              }
            }}
            disabled={frontendTranslation.isRunning || !!activeJobId}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            Force Translate All
          </button>
        </div>
      </div>

      {/* Overall Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{languages.length}</p>
          <p className="text-sm text-gray-500">Languages</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{content.length}</p>
          <p className="text-sm text-gray-500">Content Items</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-green-600">{overall.translated}</p>
          <p className="text-sm text-gray-500">Translated</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-amber-600">{overall.lowQuality}</p>
          <p className="text-sm text-gray-500">Low Quality</p>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-400">{overall.missing}</p>
          <p className="text-sm text-gray-500">Missing</p>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      ) : selectedLanguage ? (
        /* Language-specific content view */
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {LANGUAGE_NAMES[selectedLanguage] || selectedLanguage} Translations
              </h2>
              <p className="text-sm text-gray-500">
                {stats[selectedLanguage]?.translated || 0} of {content.length} translated
              </p>
            </div>
            <LanguageContentList
              language={selectedLanguage}
              content={content}
              onSelectItem={setSelectedContentId}
              selectedContentId={selectedContentId}
            />
          </div>

          {/* Detail panel - sticky so it stays visible when scrolling */}
          {selectedContentId && (
            <div className="lg:w-96 flex-shrink-0">
              <div className="lg:sticky lg:top-4">
                <TranslationDetail
                  contentId={selectedContentId}
                  language={selectedLanguage}
                  onClose={() => setSelectedContentId(null)}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Language cards grid */
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Translation Status by Language</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {languages.map((lang) => (
              <LanguageCard
                key={lang}
                language={lang}
                stats={stats[lang] || { translated: 0, lowQuality: 0, missing: 0, total: 0 }}
                isSelected={selectedLanguage === lang}
                onClick={() => setSelectedLanguage(lang)}
              />
            ))}
          </div>

          {languages.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No languages configured. Click "Translate All" to start.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// MANAGE STRINGS TAB CONTENT
// ============================================

interface StringFormData {
  key: string;
  english_text: string;
  category: string;
  description: string;
  max_length: string;
}

function StringFormModal({
  isOpen,
  editingString,
  onClose,
  onSave,
  isSaving,
}: {
  isOpen: boolean;
  editingString: ContentString | null;
  onClose: () => void;
  onSave: (data: StringFormData) => void;
  isSaving: boolean;
}) {
  const [formData, setFormData] = useState<StringFormData>({
    key: '',
    english_text: '',
    category: '',
    description: '',
    max_length: '',
  });

  useEffect(() => {
    if (editingString) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize form with prop data
      setFormData({
        key: editingString.key,
        english_text: editingString.english_text,
        category: editingString.category || '',
        description: editingString.description || '',
        max_length: editingString.max_length?.toString() || '',
      });
    } else {
      setFormData({
        key: '',
        english_text: '',
        category: '',
        description: '',
        max_length: '',
      });
    }
  }, [editingString, isOpen]);

  if (!isOpen) return null;

  const categories = ['mobile', 'extension', 'email', 'notification', 'web', 'support'];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">
            {editingString ? 'Edit String' : 'Add New String'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="string-key" className="block text-sm font-medium text-gray-700 mb-1">
              Key <span className="text-red-500">*</span>
            </label>
            <input
              id="string-key"
              type="text"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              placeholder="e.g., welcome_message"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
              disabled={!!editingString}
            />
            {editingString && (
              <p className="text-xs text-gray-500 mt-1">Key cannot be changed after creation</p>
            )}
          </div>
          <div>
            <label htmlFor="string-english" className="block text-sm font-medium text-gray-700 mb-1">
              English Text <span className="text-red-500">*</span>
            </label>
            <textarea
              id="string-english"
              value={formData.english_text}
              onChange={(e) => setFormData({ ...formData, english_text: e.target.value })}
              placeholder="The text in English..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label htmlFor="string-category" className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              id="string-category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">No category</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="string-description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              id="string-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional notes about this string..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="string-max-length" className="block text-sm font-medium text-gray-700 mb-1">Max Length</label>
            <input
              id="string-max-length"
              type="number"
              value={formData.max_length}
              onChange={(e) => setFormData({ ...formData, max_length: e.target.value })}
              placeholder="Character limit (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving && <Loader2 size={16} className="animate-spin" />}
              {editingString ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  isOpen,
  stringKey,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  isOpen: boolean;
  stringKey: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete String</h3>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <code className="bg-gray-100 px-1 rounded">{stringKey}</code>?
          This will also delete all translations for this string.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isDeleting && <Loader2 size={16} className="animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function ManageStringsTab() {
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingString, setEditingString] = useState<ContentString | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; key: string } | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const categories = ['mobile', 'extension', 'email', 'notification', 'web', 'support'];

  // Query: Fetch strings with pagination
  const { data, isLoading } = useQuery({
    queryKey: ['admin-strings', page, search, category],
    queryFn: () => api.getAdminStrings({ page, limit: 20, search, category }),
  });

  // Mutation: Create string
  const createMutation = useMutation({
    mutationFn: (formData: StringFormData) => api.createAdminString({
      key: formData.key,
      english_text: formData.english_text,
      category: formData.category || null,
      description: formData.description || null,
      max_length: formData.max_length ? parseInt(formData.max_length) : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-strings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-app-string-translation-status'] });
      setIsFormModalOpen(false);
      setStatusMessage({ type: 'success', text: 'String created successfully' });
      setTimeout(() => setStatusMessage(null), 3000);
    },
    onError: (error: Error) => {
      setStatusMessage({ type: 'error', text: error.message || 'Failed to create string' });
    },
  });

  // Mutation: Update string
  const updateMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: StringFormData }) => api.updateAdminString(id, {
      english_text: formData.english_text,
      category: formData.category || null,
      description: formData.description || null,
      max_length: formData.max_length ? parseInt(formData.max_length) : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-strings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-app-string-translation-status'] });
      setIsFormModalOpen(false);
      setEditingString(null);
      setStatusMessage({ type: 'success', text: 'String updated successfully' });
      setTimeout(() => setStatusMessage(null), 3000);
    },
    onError: (error: Error) => {
      setStatusMessage({ type: 'error', text: error.message || 'Failed to update string' });
    },
  });

  // Mutation: Delete string
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAdminString(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-strings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-app-string-translation-status'] });
      setDeleteConfirm(null);
      setStatusMessage({ type: 'success', text: 'String deleted successfully' });
      setTimeout(() => setStatusMessage(null), 3000);
    },
    onError: (error: Error) => {
      setStatusMessage({ type: 'error', text: error.message || 'Failed to delete string' });
    },
  });

  const handleSave = (formData: StringFormData) => {
    if (editingString) {
      updateMutation.mutate({ id: editingString.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const copyToClipboard = (key: string) => {
    navigator.clipboard.writeText(key);
    setStatusMessage({ type: 'success', text: 'Key copied to clipboard' });
    setTimeout(() => setStatusMessage(null), 2000);
  };

  const strings = (data?.strings || []) as ContentString[];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-6">
      {/* Status Message */}
      {statusMessage && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          statusMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {statusMessage.type === 'success' ? <Check size={20} /> : <AlertTriangle size={20} />}
          {statusMessage.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search strings..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <button
          onClick={() => {
            setEditingString(null);
            setIsFormModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus size={20} />
          Add String
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-primary-600" />
        </div>
      ) : strings.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No strings found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Key</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Text</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Translations</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {strings.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">{item.key}</code>
                      <button
                        onClick={() => copyToClipboard(item.key)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="max-w-md truncate block text-sm text-gray-700">{item.english_text}</span>
                  </td>
                  <td className="px-4 py-3">
                    {item.category ? (
                      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                        {item.category}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.translation_count}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingString(item);
                          setIsFormModalOpen(true);
                        }}
                        className="p-1 text-gray-400 hover:text-primary-600"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ id: item.id, key: item.key })}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      <StringFormModal
        isOpen={isFormModalOpen}
        editingString={editingString}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingString(null);
        }}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteConfirm}
        stringKey={deleteConfirm?.key || ''}
        onConfirm={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
        onCancel={() => setDeleteConfirm(null)}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}

// ============================================
// MAIN COMPONENT WITH TABS
// ============================================

export default function AdminTranslations() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('website-content');

  const tabs = [
    { id: 'app-strings' as TabType, label: t('admin.translations.appStrings', 'UI Strings'), icon: FileText, description: t('admin.translations.appStringsDesc', 'Web, mobile & extension') },
    { id: 'website-content' as TabType, label: t('admin.translations.websiteContent', 'CMS Content'), icon: Globe, description: t('admin.translations.websiteContentDesc', 'Editable website sections') },
    { id: 'manage-strings' as TabType, label: t('admin.translations.manageStrings', 'Manage Strings'), icon: Database, description: t('admin.translations.manageStringsDesc', 'Add, edit, delete strings') },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Translations</h1>
        <p className="text-gray-500 mt-1">Manage AI translations for all content</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                <span className="hidden sm:inline text-xs text-gray-400">({tab.description})</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'app-strings' && <AppStringsTab />}
      {activeTab === 'website-content' && <WebsiteContentTab />}
      {activeTab === 'manage-strings' && <ManageStringsTab />}
    </div>
  );
}
